import { Button, Stack, Tooltip, Box } from "@mui/material";
import { SnackbarProvider } from "notistack";
import { useState, useRef, useEffect, useCallback } from "react";
import { UploadFile } from '@mui/icons-material';
import AddNewPayroll from "./AddNew/AddNewPayroll";
import CustomPagination from "../../Components/CustomPagination";
import SetupTable from './SetupTable';
import PayrollImport from "./Import/PayrollImport";
import { getFilteredPayrollLoad, deletePayrollSetup } from "../../api";
import { buildFilteredPayrollLoadPayload } from "../../Payloads/PayrollSetupPayload";
import PayrollsPopup from "../../Components/PayrollPopup";
import { RenderMultiple } from "../../HelperFunctions/MultiplePopUpTable";
import { useParams } from "react-router-dom";
import { ITEM_TYPE_MAP } from "../../Enums/Enum";
import { calculateTotalPages } from "../../HelperFunctions/HelperFunctions";
import { useAlerts, useDebouncedEffect, usePermissions, useFetchCorporations } from "../../Hooks/index";
import { Loader } from "../../Components/Loader";
import { payrollSetupStyles } from "../../styles/payrollSetup.styles";

// --- Columns Definition for the Setup Table ---
const columns = [
    { name: 'Corporation Name', dataKey: 'corporation', width: 150 },
    { name: 'Item Type', dataKey: 'itemType', width: 150 },
    { name: 'Item Name', dataKey: 'itemName', width: 150 },
    { name: 'Department', dataKey: 'department', width: 150 },
    { name: 'Job Title', dataKey: 'jobtitle', width: 150 },
    { name: 'Expense Account', dataKey: 'account', width: 150 },
    { name: 'Payable Account', dataKey: 'liabilityAccount', width: 150 },
    { name: 'Status', dataKey: 'status', width: 150 },
];

/**
 * Transforms raw backend payroll items into table-friendly display rows.
 * Uses RenderMultiple helper to format multi-department and multi-job associations.
 */
const formatPayrollRows = (dataList) => {
    if (!dataList || dataList.length === 0) return [];
    
    return dataList.map((parentItem) => {
        const details = parentItem.Griddetails || [];
        return {
            id: parentItem.ID,
            payrollItemId: parentItem.ID,
            corporationId: parentItem.CorporationID,
            corporation: parentItem.CorporationName,
            itemType: ITEM_TYPE_MAP[parentItem.ItemType] || "Unknown",
            itemName: parentItem.ItemName,
            status: parentItem.IsActive === 1 ? "active" : "inactive",
            department: <RenderMultiple details={details} field="dept" />,
            jobtitle: <RenderMultiple details={details} field="job" />,
            account: <RenderMultiple details={details} field="acct" />,
            liabilityAccount: <RenderMultiple details={details} field="liab" />,
            rawDetails: details,
            rawParent: parentItem
        };
    });
};

const PayrollSetup = () => {
    // --- UI State (Modals/Popups) ---
    const [addNew, setAddNew] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [editData, setEditData] = useState(null);

    const addNewButtonRef = useRef(null);
    const snackbarRef = useRef(null);
    const { corpId, menuId } = useParams();

    // --- React-Scoped Query Cache (Prevents global multi-tenant pollution) ---
    const payrollTableCacheRef = useRef({});

    // --- Data State ---
    const { selectedCorp } = useFetchCorporations(corpId, { includeSelect: true });
    const [filters, setFilters] = useState({});
    const [tableRows, setTableRows] = useState([]);
    const [fetchLoading, setFetchLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Custom Hooks for Alerts and Permissions
    const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();
    const { permissions, loading: permsLoading } = usePermissions(menuId);

    const showSnackbar = useCallback((message, options = {}) => {
        snackbarRef.current?.enqueueSnackbar(message, options);
    }, []);

    const isLoading = permsLoading || fetchLoading;

    // Invalidate component cache whenever selected corporation changes
    useEffect(() => {
        payrollTableCacheRef.current = {};
    }, [selectedCorp?.ID]);

    // --- Data Fetching with Debounce ---
    useDebouncedEffect(() => {
        if (!selectedCorp || permsLoading) return;

        let cancelled = false;

        const fetchPayrollData = async () => {
            setFetchLoading(true);

            const payload = buildFilteredPayrollLoadPayload(
                filters,
                selectedCorp,
                page
            );

            try {
                // Build a deterministic cache key based on corporation, page, and active filters
                const cacheKey = JSON.stringify({
                    corp: selectedCorp?.ID,
                    page,
                    filters: {
                        itemType: filters?.itemType?.term || "",
                        itemName: filters?.itemName?.term || "",
                        department: filters?.department?.term || "",
                        jobTitle: filters?.jobTitle?.term || "",
                        account: filters?.account?.term || "",
                        liabilityAccount: filters?.liabilityAccount?.term || "",
                        status: filters?.status?.term || ""
                    }
                });

                let data;
                if (payrollTableCacheRef.current[cacheKey]) {
                    data = payrollTableCacheRef.current[cacheKey];
                } else {
                    data = await getFilteredPayrollLoad(payload);
                    // Keep cache bounded to max 50 entries
                    const cacheKeys = Object.keys(payrollTableCacheRef.current);
                    if (cacheKeys.length > 50) {
                        delete payrollTableCacheRef.current[cacheKeys[0]];
                    }
                    payrollTableCacheRef.current[cacheKey] = data;
                }

                if (cancelled) return;

                let dataList = [];
                if (Array.isArray(data)) {
                    dataList = data;
                } else if (data && data.ID) {
                    dataList = [data];
                }

                if (dataList.length === 0) {
                    setTableRows([]);
                    setTotalPages(1);
                    setFetchLoading(false);
                    return;
                }

                const newTotalPages = calculateTotalPages(dataList);
                setTableRows(formatPayrollRows(dataList));
                setTotalPages(newTotalPages);
                setFetchLoading(false);
            } catch (err) {
                console.error("API error while fetching payroll setup data:", err);
                if (!cancelled) {
                    showSnackbar(err.message || "An error occurred while fetching data.", { variant: "error" });
                    setTableRows([]);
                    setFetchLoading(false);
                }
            }
        };

        const timer = setTimeout(() => {
            fetchPayrollData();
        }, 500);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [filters, selectedCorp?.ID, page, refreshKey, permsLoading, showSnackbar], 500);

    // --- Refresh Handler ---
    const handleRefresh = useCallback(() => {
        payrollTableCacheRef.current = {};
        setFilters({});
        setPage(0);
        setRefreshKey(prev => prev + 1);
    }, []);

    // Alt + F5 Keyboard Shortcut for Refresh
    useEffect(() => {
        const handleKeyDown = (event) => {
            const isAltF5 = event.altKey && event.key === "F5";
            if (isAltF5) {
                event.preventDefault();
                event.stopPropagation();
                handleRefresh();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [handleRefresh]);

    // --- Modal Close Handlers ---
    const handleCloseAddNew = useCallback(() => {
        setAddNew(false);
    }, []);

    const handleCloseEdit = useCallback(() => {
        setEditOpen(false);
        setEditData(null);
    }, []);

    const handleCloseImport = useCallback(() => {
        setImportOpen(false);
        payrollTableCacheRef.current = {};
        setRefreshKey(prev => prev + 1);
    }, []);

    // --- User Actions ---
    const handleDeleteAction = useCallback(async (row) => {
        try {
            setActionLoading(true);
            const response = await deletePayrollSetup(row.payrollItemId || row.ID || row.id);
            if (response?.StatusCode === 200) {
                showSnackbar(response?.Message || response?.Status || "Payroll item deleted successfully.", { variant: "success" });
                payrollTableCacheRef.current = {}; // Clear cache on delete
                setRefreshKey(prev => prev + 1);   // Re-fetch without clearing user filters
            } else {
                showSnackbar(response?.Message || response?.Status || "Failed to delete payroll item.", { variant: "error" });
            }
        } catch (error) {
            showSnackbar(error?.response?.data?.Status || error?.response?.data?.Message || error?.message || "Failed to delete payroll item.", { variant: "error" });
        } finally {
            setActionLoading(false);
        }
    }, [showSnackbar]);

    const handleDeleteRow = useCallback((row) => {
    if (!permissions.canDelete) {
        showSnackbar(
            "You don't have permission to Delete Payroll Setup.",
            { variant: "error" }
        );

        return;
    }

    showDeleteConfirm(
        "Are you sure you want to delete this payroll item?",
        () => handleDeleteAction(row)
    );
}, [
    permissions.canDelete,
    showDeleteConfirm,
    handleDeleteAction,
    showSnackbar
]);

    const handleOpenImport = useCallback(() => {
    if (!permissions.canCreate) {
        showSnackbar(
            "Upload template is not allowed.",
            { variant: "error" }
        );

        return;
    }

    setImportOpen(true);
}, [permissions.canCreate, showSnackbar]);

    const handleOpenAddNew = useCallback(() => {
    if (!permissions.canCreate) {
        console.warn("User does not have permission to add new payroll setup.");

        showSnackbar(
            "You don't have permission to Add Payroll Setup.",
            { variant: "error" }
        );

        return;
    }

    document.activeElement?.blur();
    setAddNew(true);
}, [permissions.canCreate, showSnackbar]);

    const handleEditRow = useCallback((row) => {
    if (!permissions.canUpdate) {
        showSnackbar(
            "You don't have permission to Edit Payroll Setup.",
            { variant: "error" }
        );

        return;
    }

    setEditOpen(true);
    setEditData({
        ...row.rawParent,
        payrollItemId: row.id
    });
}, [permissions.canUpdate, showSnackbar]);

    return (
        <SnackbarProvider
            ref={snackbarRef}
            maxSnack={1}
            anchorOrigin={{
                vertical: "top",
                horizontal: "right",
            }}
            autoHideDuration={2000}
        >
            {isLoading && <Loader loading={isLoading} />}
            {actionLoading && <Loader loading={actionLoading} />}
            <Stack height="100%" gap={0.5} padding={0}>
                <Stack alignSelf={"flex-end"} direction="row" gap={1} alignItems="center">
                    <Tooltip title="Import">
                        <span title="Import" style={{ display: "inline-flex" }}>
                            <UploadFile
                                onClick={handleOpenImport}
                                aria-label="Import Payroll Setup"
                                sx={payrollSetupStyles.uploadIcon}
                            />
                        </span>
                    </Tooltip>
                    <span title="Add New">
                        <Button
                            size="small"
                            sx={payrollSetupStyles.addNewButton}
                            color="secondary"
                            variant="contained"
                            disableElevation
                            ref={addNewButtonRef}
                            onClick={handleOpenAddNew}
                            aria-label="Add New Payroll Setup"
                        >
                            Add New
                        </Button>
                    </span>
                </Stack>

                <Stack flexGrow={1} sx={payrollSetupStyles.tableWrapperStack}>
                    <Box sx={payrollSetupStyles.tableWrapperBox}>
                        <SetupTable
                            rows={tableRows}
                            columns={columns}
                            filters={filters}
                            loading={isLoading}
                            onFilterChange={(newFilters) => {
                                payrollTableCacheRef.current = {};
                                setFilters(newFilters);
                                setPage(0);
                            }}
                            onEdit={handleEditRow}
                            onDelete={handleDeleteRow}
                        />
                    </Box>
                    <Stack direction="row" justifyContent="flex-end" alignItems="center"
                        sx={payrollSetupStyles.paginationFooter}>
                        <CustomPagination
                            count={totalPages}
                            page={page}
                            onChange={(e, newPage) => setPage(newPage - 1)}
                        />
                    </Stack>
                </Stack>
            </Stack>

            {/* Import Dialog */}
            {importOpen && (
                <PayrollImport
                    open={importOpen}
                    onClose={handleCloseImport}
                    selectedCorp={selectedCorp}
                    onSuccess={() => {
                        payrollTableCacheRef.current = {};
                        setRefreshKey(prev => prev + 1);
                    }}
                />
            )}

            {/* Add New Dialog */}
            {addNew && (
                <AddNewPayroll
                    open={addNew}
                    fullScreen
                    onClose={handleCloseAddNew}
                    selectedCorp={selectedCorp}
                    anchorEl={addNewButtonRef.current}
                    onSuccess={() => {
                        payrollTableCacheRef.current = {}; // Invalidate cache on success
                        setRefreshKey(prev => prev + 1);
                    }}
                    permissions={permissions}
                />
            )}

            {/* Edit Dialog */}
            {editOpen && (
                <AddNewPayroll
                    open={editOpen}
                    fullScreen
                    onClose={handleCloseEdit}
                    editData={editData}
                    onDeleteSuccess={() => {
                        payrollTableCacheRef.current = {}; // Invalidate cache on success
                        setRefreshKey(prev => prev + 1);
                    }}
                    onSuccess={() => {
                        payrollTableCacheRef.current = {}; // Invalidate cache on success
                        setRefreshKey(prev => prev + 1);
                    }}
                    permissions={permissions}
                />
            )}
            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={hidePopup}
            />

        </SnackbarProvider>
    );
};

export default PayrollSetup;
